import { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiPackage, FiUser, FiRefreshCw } from 'react-icons/fi';
import { apiAdminGetReturnReasons, apiGetAllReturns, apiUpdateReturnStatus } from '../api';
import Pagination from '../components/Pagination';
import { getAdminBookImage, handleAdminBookImageError } from '../utils/bookImage';
import ReturnPanel from '../components/ReturnPanel';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'refunded', label: 'Refunded' },
];

const statusStyles = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700',
  refunded: 'bg-green-100 text-green-700',
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return ''; }
};

const fmtINR = (n) =>
  `\u20B9${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Dedicated returns dashboard. Bulk overview that complements the inline
// ReturnPanel inside OrderDetailDrawer (which only shows one order at a time).
const ReturnsManager = () => {
  const [returns, setReturns] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [reasonLabels, setReasonLabels] = useState({});

  useEffect(() => {
    apiAdminGetReturnReasons().then((data) => setReasonLabels(Object.fromEntries((data.reasons || []).map((reason) => [reason.code, reason.label])))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGetAllReturns({
        page, limit: 20, status, search: appliedSearch,
      });
      setReturns(data.returns || []);
      setPagination(data.pagination || {});
    } catch { /* API client displays the error toast. */ } finally {
      setLoading(false);
    }
  }, [page, status, appliedSearch]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (id, newStatus, note, refundAmount, shippingRefundAmount, otherRefundAmount) => {
    try {
      const data = await apiUpdateReturnStatus(id, newStatus, note, refundAmount, shippingRefundAmount, otherRefundAmount);
      setReturns((prev) => prev.map((r) =>
        r.id === id ? { ...r, ...data.returnRequest } : r
      ));
    } catch { /* API client displays the error toast. */ }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  };

  // Quick counts for the summary row.
  const counts = returns.reduce(
    (acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; },
    {}
  );

  return (
    <div className="space-y-4">
      {/* Filters */}
      <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-black" size={14} />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by Return ID..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <select
          value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button
          type="submit"
          className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700"
        >
          Search
        </button>
        <button
          type="button"
          onClick={load}
          className="text-black hover:text-orange-600 p-2"
          title="Refresh"
        >
          <FiRefreshCw size={16} />
        </button>
      </form>

      {/* Counts strip */}
      <div className="flex flex-wrap gap-2 text-xs">
        {STATUS_OPTIONS.slice(1).map((s) => (
          <span
            key={s.value}
            className={`px-2.5 py-1 rounded-full font-medium ${statusStyles[s.value]}`}
          >
            {s.label}: {counts[s.value] || 0}
          </span>
        ))}
        <span className="ml-auto text-black self-center">
          {pagination.total == null ? '' : `${pagination.total} total`}
        </span>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
        </div>
      ) : (
        <div className="space-y-3">
          {returns.map((r) => {
            const expanded = expandedId === r.id;
            const lineTotal = r.orderItem
              ? Number(r.orderItem.price) * Number(r.quantity)
              : null;
            return (
              <article
                key={r.id}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : r.id)}
                  className="w-full text-left p-4 hover:bg-gray-50 transition flex items-start gap-4"
                >
                  <img
                    src={getAdminBookImage(r.orderItem?.image)}
                    onError={handleAdminBookImageError}
                    alt={`${r.orderItem?.title || 'Book'} cover`}
                    className="w-14 h-14 object-cover rounded border border-gray-200 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-black">{r.returnId}</span>
                      <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${statusStyles[r.status]}`}>
                        {r.status}
                      </span>
                      <span className="text-xs text-black">·</span>
                      <span className="text-xs text-black">{fmtDate(r.createdAt)}</span>
                    </div>
                    <p className="font-medium text-black mt-1 truncate">
                      {r.orderItem?.title || `Book #${r.bookId}`}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-black mt-1 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <FiUser size={11} /> {r.user?.name || `User #${r.userId}`}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <FiPackage size={11} /> Order {r.order?.orderId || r.orderId}
                      </span>
                      <span>Qty: {r.quantity}</span>
                      {lineTotal != null && <span>{fmtINR(lineTotal)}</span>}
                      <span>Reason: {reasonLabels[r.reason] || r.reason}</span>
                    </div>
                  </div>
                  <span className="text-xs text-black flex-shrink-0">
                    {expanded ? 'Close' : 'Manage'}
                  </span>
                </button>

                {expanded && (
                  <div className="px-4 pb-4 pt-0">
                    {r.description && (
                      <p className="text-xs text-black bg-gray-50 rounded p-2 mb-2 whitespace-pre-wrap">
                        <strong>Customer note:</strong> {r.description}
                      </p>
                    )}
                    <ReturnPanel returnRequest={r} onSave={handleSave} />
                  </div>
                )}
              </article>
            );
          })}

          {returns.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200 text-black">
              No return requests found
            </div>
          )}
        </div>
      )}

      <Pagination
        page={page}
        pages={pagination.pages || 1}
        onChange={setPage}
      />
    </div>
  );
};

export default ReturnsManager;
